import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
    #cartCount = 0;
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return this.#cartCount;
  }

  set currentCartCount(value) {
 const numericValue = Number(value);
    const normalizedValue = Number.isFinite(numericValue) ? numericValue : 0;

    this.#cartCount = normalizedValue;
    this.refs.cartBubbleCount.textContent = normalizedValue < 100 ? String(normalizedValue) : '';
    }

  connectedCallback() {
    super.connectedCallback();

    const initialCount = parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
    this.#cartCount = Number.isFinite(initialCount) ? initialCount : 0;

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const eventData = event.detail.data ?? {};
    const comingFromProductForm = eventData.source === 'product-form-component';

    let itemCount = Number(eventData.itemCount);

    if (!Number.isFinite(itemCount)) {
      if (!comingFromProductForm) return;

      const quantityAdded = Number(eventData.quantityAdded);

      if (!Number.isFinite(quantityAdded)) return;

      itemCount = this.currentCartCount + quantityAdded;
    }

    itemCount = Math.max(0, Math.round(itemCount));

    const shouldAnimate = !eventData.didError;
    this.renderCartBubble(itemCount, shouldAnimate);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} animate - Whether to animate the cart bubble update.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    const normalizedItemCount = Math.max(0, Math.round(itemCount));

    this.refs.cartBubbleCount.classList.toggle('hidden', normalizedItemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', normalizedItemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', normalizedItemCount > 0 && animate);

    this.currentCartCount = normalizedItemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', normalizedItemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
